<template>
  <div>
    <div class="content-box">
      <div class="list" v-infinite-scroll="fetchListPage" infinite-scroll-disabled="busy" infinite-scroll-distance="10">
        <!-- Skeleton enquanto carrega a 1ª página (troca de modo/tag/busca) -->
        <template v-if="initialLoading">
          <div class="list-item skel-card" v-for="n in 7" :key="'skel-' + n">
            <div class="skel skel-avatar"></div>
            <div class="item-info">
              <div class="skel skel-line sk-name"></div>
              <div class="skel skel-line sk-email"></div>
              <div class="skel skel-line sk-tag"></div>
            </div>
            <div class="skel skel-btn"></div>
          </div>
        </template>
        <div
          class="list-item"
          v-for="item in attendeeList"
          :key="'item-' + item.id"
          @click="navToItem(item)"
        >
          <!-- Avatar (estado por modo) -->
          <div class="avatar" :class="av(item).cls">
            {{ initials(item.name) }}
            <div v-if="av(item).badge" class="avatar-badge">
              <svg v-if="av(item).badge === 'check'" width="9" height="9" viewBox="0 0 16 16" fill="none">
                <path d="M4 8l3 3 5-6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <svg v-else width="9" height="9" viewBox="0 0 16 16" fill="none">
                <path d="M6 4l5 4-5 4" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>

          <!-- Info + status -->
          <div class="item-info">
            <div class="item-name">{{ item.name }}</div>
            <div class="item-email">{{ item.email }}</div>
            <div :class="tag(item).cls">{{ tag(item).text }}</div>
          </div>

          <!-- Ação (por modo) -->
          <div class="item-action">
            <button
              type="button"
              :class="btn(item).cls"
              :disabled="item.onAction"
              @click.stop.prevent="onAction(item)"
            >
              <svg v-if="btn(item).icon === 'check'" width="12" height="12" viewBox="0 0 16 16" fill="none">
                <path d="M4 8l3 3 5-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <svg v-else-if="btn(item).icon === 'logout'" width="12" height="12" viewBox="0 0 16 16" fill="none">
                <path d="M10 4l-6 8M4 4l6 8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
              <svg v-else-if="btn(item).icon === 'plus'" width="12" height="12" viewBox="0 0 16 16" fill="none">
                <path d="M8 2v12M2 8h12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
              <svg v-else width="12" height="12" viewBox="0 0 16 16" fill="none">
                <path d="M3 7h10v6a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" stroke="currentColor" stroke-width="1.4"/>
                <path d="M2 5h12v2H2zM8 5v10" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
              </svg>
              {{ btn(item).label }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <index-modal ref="modalFilter" @setFilter="setFilterTag"></index-modal>
  </div>
</template>

<script>
import infiniteScroll from 'vue-infinite-scroll'
import IndexModal from '@/modules/check-in/pages/index/index-modal'
import { fetchAttendeeList } from '@/modules/check-in/service/fetchAttendeeList.ts'
import { fetchTags } from '@/modules/check-in/service/fetchTags'
import { putManualCheckIn } from '@/modules/check-in/service/putManualCheckIn'
import { putManualCheckOut } from '@/modules/check-in/service/putManualCheckOut'
import { fetchCounts } from '@/modules/check-in/service/fetchCounts'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import { mapGetters, mapMutations } from 'vuex'
import { CHECK_IN_MODULE } from '@/modules/check-in'

export default {
  name: 'index-list',
  directives: { infiniteScroll },
  components: { IndexModal },
  data: () => ({
    pagination: { currentPage: 0, perPage: 30, totalPages: 0 },
    onReady: false,
    attendeeList: [],
    tagsList: [],
    filterTags: [],
    selectedItem: null,
    busy: false,
    // Token de requisição: cada reset (busca/modo/filtro) o incrementa. Respostas
    // que voltam com token antigo são descartadas — evita resultado "fora de ordem"
    // (o clássico "some/precisa apagar letra pra achar").
    reqToken: 0,
    currentFilter: { search: null, isStatusUnseen: null, isStatusCheckIn: null }
  }),
  computed: {
    ...mapGetters(CHECK_IN_MODULE, ['stationMode', 'brindeTag', 'brindeTags', 'brindeFilter', 'leadCollectedSignal']),
    listItemsDetail () {
      return this.$store.state[CHECK_IN_MODULE].list
    },
    // Carregando a 1ª página (após reset por troca de modo/tag/busca): nada na
    // tela ainda e uma requisição em andamento → mostra os cards skeleton.
    initialLoading () {
      return this.busy && this.attendeeList.length === 0
    }
  },
  watch: {
    listItemsDetail: {
      handler (listItemsDetail) {
        this.attendeeList
          .filter(({ id }) => !!listItemsDetail[id])
          .forEach(item => this.updateItemStatus(item, listItemsDetail[item.id]))
      },
      deep: true
    },
    // Trocar de modo (ou a tag do brinde) recarrega a lista pra refletir os
    // flags/filtros corretos daquele modo.
    stationMode () {
      this.resetList()
      this.fetchListPage()
    },
    // Trocar o filtro de brinde (chips) recarrega a lista escopada àquele brinde.
    brindeFilter () {
      if (this.stationMode === 'brinde') {
        this.resetList()
        this.fetchListPage()
      }
    },
    // Coleta de lead concluída no sidebar: marca o item na lista e atualiza os
    // contadores em tempo real (sem precisar recarregar a página).
    'leadCollectedSignal.tick' () {
      const sig = this.leadCollectedSignal
      if (!sig || !sig.id) return
      const item = this.attendeeList.find(i => String(i.id) === String(sig.id))
      if (item) item.leadCollected = true
      this.refreshCounts()
    }
  },
  methods: {
    ...mapMutations(CHECK_IN_MODULE, ['setCheckinCounts']),
    // Atualiza os contadores das abas do modo atual (respeita busca + tag).
    async refreshCounts () {
      const token = this.reqToken
      try {
        const c = await fetchCounts(
          this.stationMode,
          this.currentFilter.search,
          this.brindeFilter
        )
        // Descarta contagem obsoleta se um novo reset já ocorreu.
        if (token === this.reqToken) {
          this.setCheckinCounts(c)
        }
      } catch (e) { /* silencioso: contadores são cosméticos */ }
    },
    // ─── Helpers de exibição por modo ───
    initials (name) {
      if (!name) return '?'
      const parts = name.trim().split(/\s+/).filter(Boolean)
      if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    },
    av (item) {
      if (this.stationMode === 'lead') {
        return item.leadCollected ? { cls: 'avatar lead-collected', badge: 'check' } : { cls: 'avatar gray', badge: null }
      }
      if (this.stationMode === 'brinde') {
        const s = this.brindeState(item)
        if (s === 'complete') return { cls: 'avatar brinde-delivered', badge: 'check' }
        if (s === 'partial') return { cls: 'avatar brinde-partial', badge: null }
        return { cls: 'avatar gray', badge: null }
      }
      if (item.isStatusCheckIn) return { cls: 'avatar checked-in', badge: 'check' }
      if (item.isStatusCheckOut) return { cls: 'avatar checked-out', badge: 'arrow' }
      return { cls: 'avatar gray', badge: null }
    },
    // Estado do brinde na lista, sobre o catálogo:
    //  nogift  = não é elegível a nenhum brinde
    //  pending = elegível mas não retirou nada
    //  partial = retirou alguns, mas não todos
    //  complete= retirou todos os elegíveis
    brindeState (item) {
      const elig = item.giftEligible || 0
      const done = item.giftDone || 0
      if (elig === 0) return 'nogift'
      if (done === 0) return 'pending'
      if (done < elig) return 'partial'
      return 'complete'
    },
    brindeCountLabel (item) {
      const elig = item.giftEligible || 0
      const done = item.giftDone || 0
      return elig > 1 ? ` (${done}/${elig})` : ''
    },
    tag (item) {
      if (this.stationMode === 'lead') {
        return item.leadCollected
          ? { cls: 'lead-tag existing', text: 'Coletado' }
          : { cls: 'lead-tag new', text: 'Aguardando' }
      }
      if (this.stationMode === 'brinde') {
        const s = this.brindeState(item)
        const single = this.brindeFilter != null
        const n = this.brindeCountLabel(item)
        if (s === 'complete') return { cls: 'brinde-tag done', text: (single ? 'Entregue' : 'Totalmente retirado') + n }
        if (s === 'partial') return { cls: 'brinde-tag partial', text: 'Parcialmente retirado' + n }
        if (s === 'pending') return { cls: 'brinde-tag pending', text: 'Aguardando retirada' + n }
        return { cls: 'brinde-tag ineligible', text: single ? 'Não elegível' : 'Sem brinde' }
      }
      if (item.isStatusCheckIn) return { cls: 'checkin-tag done', text: 'Check-in realizado' }
      if (item.isStatusCheckOut) return { cls: 'checkin-tag out', text: 'Check-out realizado' }
      return { cls: 'checkin-tag pending', text: 'Aguardando' }
    },
    btn (item) {
      if (this.stationMode === 'lead') {
        return item.leadCollected
          ? { cls: 'btn-lead collected', icon: 'check', label: 'Coletado' }
          : { cls: 'btn-lead', icon: 'plus', label: 'Coletar' }
      }
      if (this.stationMode === 'brinde') {
        const s = this.brindeState(item)
        if (s === 'complete') return { cls: 'btn-brinde delivered', icon: 'check', label: 'Concluído' }
        if (s === 'partial') return { cls: 'btn-brinde', icon: 'gift', label: 'Brindes' }
        if (s === 'pending') return { cls: 'btn-brinde', icon: 'gift', label: 'Entregar' }
        return { cls: 'btn-brinde ineligible', icon: 'gift', label: 'Ver' }
      }
      return item.isStatusCheckIn
        ? { cls: 'btn-checkout', icon: 'logout', label: 'Check Out' }
        : { cls: 'btn-checkin', icon: 'check', label: 'Check In' }
    },

    // ─── Ações por modo ───
    onAction (item) {
      if (this.stationMode === 'lead') {
        // Abre a ficha (o sidebar em modo Lead cai direto no formulário).
        this.navToItem(item)
        return
      }
      if (this.stationMode === 'brinde') {
        this.brindeAction(item)
        return
      }
      this.checkTag(item)
    },
    // Brinde: entrega/autorização vivem na ficha (checklist do catálogo). O toque
    // na lista sempre a abre.
    brindeAction (item) {
      this.navToItem(item)
    },

    // ─── Check-in (fluxo original) ───
    checkTag (item) {
      if (this.tagsList.length === 0) {
        this.actionClickHandle(item)
        return
      }
      this.selectedItem = item
      this.$refs.modalFilter.open()
    },
    setFilterTag (filter) {
      this.filterTags = filter
      if (this.selectedItem) {
        this.actionClickHandle(this.selectedItem)
      }
    },
    async actionClickHandle (item) {
      item.onAction = true
      let action = putManualCheckIn
      if (item.isStatusCheckIn) {
        action = putManualCheckOut
      }
      try {
        const { message, ok } = await action(item.id, [this.filterTags.tag])
        if (!ok) {
          errorMessage(message ?? 'Ocorreu algum erro ao realizar essa ação.')
        } else {
          successMessage(message ?? 'Ação realizada com sucesso.')
          item.isStatusCheckIn = !item.isStatusCheckIn
          item.isStatusCheckOut = !item.isStatusCheckIn
          this.refreshCounts()
        }
      } catch ({ serverMessage }) {
        errorMessage(serverMessage ?? 'Ocorreu algum erro ao realizar essa ação.')
      } finally {
        item.onAction = false
      }
    },

    navToItem ({ id }) {
      this.$store.commit(CHECK_IN_MODULE + '/setShowDetailId', id)
    },
    filter (search, isStatusUnseen, isStatusCheckIn) {
      this.currentFilter.search = search
      this.currentFilter.isStatusUnseen = isStatusUnseen
      this.currentFilter.isStatusCheckIn = isStatusCheckIn
      this.resetList()
      this.fetchListPage()
    },
    async fetchListTags () {
      try {
        const response = await fetchTags()
        this.tagsList.push(...response)
      } catch ({ serverMessage }) {
        errorMessage(serverMessage ?? 'Ocorreu algum erro ao realizar essa ação.')
      }
    },
    async fetchListPage () {
      // Fim das páginas: nada a carregar (não deixa o busy preso).
      if (this.pagination.totalPages !== null &&
          this.pagination.currentPage >= this.pagination.totalPages) {
        return
      }
      this.busy = true
      const token = this.reqToken
      const page = this.pagination.currentPage + 1
      // Primeira página de um reset (troca de modo/tag/busca): atualiza contadores.
      if (page === 1) {
        this.refreshCounts()
      }
      try {
        const response = await fetchAttendeeList(
          { page, limit: this.pagination.perPage },
          {
            search: this.currentFilter.search,
            isStatusCheckIn: this.currentFilter.isStatusCheckIn,
            isStatusUnseen: this.currentFilter.isStatusUnseen,
            mode: this.stationMode,
            brindeTagId: this.brindeFilter
          }
        )
        // Um reset (nova busca/modo/filtro) aconteceu enquanto esperávamos:
        // descarta esta resposta obsoleta (não polui a lista atual).
        if (token !== this.reqToken) {
          return
        }
        this.pagination.currentPage = response.meta.currentPage
        this.pagination.totalPages = response.meta.totalPages
        // Inicializa os flags de lead/brinde pra serem reativos mesmo se o backend
        // ainda não os retornar (default: pendente).
        this.attendeeList.push(...response.list.map(item => ({
          ...item,
          onAction: false,
          leadCollected: !!item.leadCollected,
          giftEligible: item.giftEligible || 0,
          giftDone: item.giftDone || 0
        })))
      } catch (e) {
        /* silencioso: a próxima interação recarrega */
      } finally {
        // Só libera o busy se esta ainda for a requisição vigente.
        if (token === this.reqToken) {
          this.busy = false
        }
      }
    },
    updateItemStatus (item, itemDetail) {
      item.isStatusCheckIn = itemDetail.isStatusCheckIn
      item.isStatusCheckOut = itemDetail.isStatusCheckOut
    },
    resetList () {
      this.reqToken++ // invalida qualquer requisição em voo
      this.busy = false
      this.attendeeList = []
      this.pagination.currentPage = 0
      this.pagination.totalPages = null
    }
  },
  created () {
    this.pagination.currentPage = 0
    this.pagination.totalPages = null
    this.fetchListPage()
    this.fetchListTags()
  }
}
</script>

<style lang="scss" scoped>
.list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* Folga para o último item não ficar atrás da barra flutuante inferior. */
  padding-bottom: 110px;
}

.list-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid #e8eaef;
  border-radius: 8px;
  transition: box-shadow 0.2s;
  cursor: pointer;

  &:hover { box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04); }
}

/* ── Avatar ── */
.avatar {
  width: 42px;
  height: 42px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 800;
  flex-shrink: 0;
  color: #fff;
  position: relative;

  &.gray { background: #d1d5db; color: #6b7280; }
  &.checked-in { background: rgba(16, 185, 129, 0.1); color: #10b981; border: 2px solid rgba(16, 185, 129, 0.35); }
  &.checked-out { background: rgba(234, 138, 23, 0.1); color: #ea8a17; border: 2px solid rgba(234, 138, 23, 0.35); }
  &.lead-collected { background: rgba(79, 110, 247, 0.1); color: #4f6ef7; border: 2px solid rgba(79, 110, 247, 0.35); }
  &.brinde-delivered { background: rgba(16, 185, 129, 0.1); color: #10b981; border: 2px solid rgba(16, 185, 129, 0.35); }
  &.brinde-partial { background: rgba(232, 134, 12, 0.1); color: #e8860c; border: 2px solid rgba(232, 134, 12, 0.35); }
}

.avatar-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  box-shadow: 0 0 0 2px #fff;
  z-index: 1;
}

.avatar.checked-in .avatar-badge { background: #10b981; }
.avatar.checked-out .avatar-badge { background: #ea8a17; }
.avatar.lead-collected .avatar-badge { background: #4f6ef7; }
.avatar.brinde-delivered .avatar-badge { background: #10b981; }

/* ── Info ── */
.item-info {
  flex: 1;
  min-width: 0;
}

.item-name {
  font-size: 0.8125rem;
  font-weight: 700;
  color: #1a1d26;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-email {
  font-size: 0.6875rem;
  color: #9ca3b4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.checkin-tag, .lead-tag, .brinde-tag {
  font-size: 0.5625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-top: 4px;
}

.checkin-tag.pending { color: #9ca3b4; }
.checkin-tag.done { color: #10b981; }
.checkin-tag.out { color: #ea8a17; }
.lead-tag.new { color: #9ca3b4; }
.lead-tag.existing { color: #4f6ef7; }
.brinde-tag.pending { color: #9ca3b4; }
.brinde-tag.partial { color: #e8860c; }
.brinde-tag.done { color: #10b981; }
.brinde-tag.ineligible { color: #b0b5c2; }

/* ── Ação ── */
.item-action {
  flex-shrink: 0;
  width: 110px;
}

.btn-checkin, .btn-checkout, .btn-lead, .btn-brinde {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  border: 1.5px solid #e4e7ed;
  background: #fff;
  font-family: inherit;
  font-size: 0.6875rem;
  font-weight: 700;
  color: #4a5068;
  cursor: pointer;
  transition: all 0.2s;

  &:disabled { opacity: 0.6; cursor: default; }
}

/* Check-in */
.btn-checkin svg { color: #10b981; }
.btn-checkin:hover:not(:disabled) { border-color: #10b981; background: #f0fdf6; color: #10b981; }

/* Check-out */
.btn-checkout { border-color: #fde4c4; background: #fffaf4; color: #ea8a17; }
.btn-checkout svg { color: #ea8a17; }
.btn-checkout:hover:not(:disabled) { border-color: #ea8a17; background: #ea8a17; color: #fff; svg { color: #fff; } }

/* Lead */
.btn-lead svg { color: #4f6ef7; }
.btn-lead:hover:not(:disabled) { border-color: #4f6ef7; background: #f6f8ff; color: #4f6ef7; }
.btn-lead.collected { border-color: #4f6ef7; background: #4f6ef7; color: #fff; svg { color: #fff; } }

/* Brinde */
.btn-brinde svg { color: #e8860c; }
.btn-brinde:hover:not(:disabled) { border-color: #e8860c; background: #fffaf4; color: #e8860c; }
.btn-brinde.delivered { border-color: #10b981; background: #10b981; color: #fff; svg { color: #fff; } }
/* Não elegível: ação de autorizar (aparência neutra, sem cor de status) */
.btn-brinde.ineligible { color: #9098a8; }
.btn-brinde.ineligible svg { color: #b0b5c2; }
.btn-brinde.ineligible:hover:not(:disabled) { border-color: #c0c4d0; background: #f8f9fb; color: #6b7280; }

/* ── Skeleton dos cards (glass shimmer) ── */
.skel-card { cursor: default; }
.skel-card:hover { box-shadow: none; }

.skel {
  position: relative;
  overflow: hidden;
  background: #eef1f6;
}
.skel::after {
  content: '';
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.65), transparent);
  animation: skel-shimmer 1.3s ease-in-out infinite;
}
@keyframes skel-shimmer {
  100% { transform: translateX(100%); }
}

.skel-avatar {
  width: 42px;
  height: 42px;
  border-radius: 8px;
  flex-shrink: 0;
}
.skel-line {
  height: 10px;
  border-radius: 5px;
  margin-bottom: 7px;
}
.sk-name { width: 45%; height: 13px; }
.sk-email { width: 65%; }
.sk-tag { width: 30%; height: 8px; margin-bottom: 0; }
.skel-btn {
  width: 110px;
  height: 34px;
  border-radius: 8px;
  flex-shrink: 0;
}
</style>
